/**
 * Paleta de colores definitiva para estados de albaranes y materiales
 * Centralizada para mantener consistencia en todo el proyecto
 */

// Estados de Albarán
const ESTADO_COLORS_ALBARAN = {
  'pendiente_asignar': '#fd7e14',      // Naranja - Pendiente asignar
  'asignado': '#6c757d',               // Gris - Asignado
  'entregado': '#007bff',              // Azul - Entregado
  'devuelto': '#28a745',               // Verde - Devuelto
  'pendiente_aceptar_devolucion': '#6f42c1', // Morado - Pendiente Aceptar Devolución
  'cerrado': 'transparent',            // Transparente - Cerrado
  'cerrado_sin_tarea': 'transparent',  // Transparente - Cerrado sin tarea
  'incidencia': '#dc3545',             // Rojo - Con incidencia
  'material_abandonado': '#dc3545', // Igual que cerrado: verde/azul en listado según es_facturable
};

// Estados de Material
// Los estados que coinciden con albarán usan los mismos colores
const ESTADO_COLORS_MATERIAL = {
  'pendiente_aceptar': '#fd7e14',      // Naranja - Esperando acción
  'entregado': '#007bff',              // Azul - Entregado (igual que albarán)
  'devuelto': '#28a745',               // Verde - Devuelto (igual que albarán)
  'rechazado': '#dc3545',              // Rojo - Rechazado
  'pendiente_aceptar_devolucion': '#6f42c1', // Morado - Pendiente Aceptar Devolución (igual que albarán)
  'no_devuelto': '#dc3545',            // Rojo - No devuelto
  'consumido': '#17a2b8',              // Azul turquesa - Consumido
  'incidencia': '#dc3545'              // Rojo - Con incidencia (igual que albarán)
};

// Objeto combinado para compatibilidad (prioridad: albarán primero, luego material)
export const ESTADO_COLORS = {
  ...ESTADO_COLORS_ALBARAN,
  ...ESTADO_COLORS_MATERIAL
};

/**
 * Obtiene el color para un estado específico
 * @param {string} estado - El estado del albarán o material
 * @param {string} tipo - 'albaran' o 'material' (opcional, para diferenciar estados duplicados)
 * @returns {string} - El color hexadecimal correspondiente
 */
export function getEstadoColor(estado, tipo = null) {
  // Si se especifica el tipo, usar el objeto correspondiente
  if (tipo === 'albaran') {
    return ESTADO_COLORS_ALBARAN[estado] || ESTADO_COLORS_MATERIAL[estado] || '#6c757d';
  }
  if (tipo === 'material') {
    return ESTADO_COLORS_MATERIAL[estado] || ESTADO_COLORS_ALBARAN[estado] || '#6c757d';
  }
  // Por defecto, priorizar albarán si existe, sino material
  return ESTADO_COLORS_ALBARAN[estado] || ESTADO_COLORS_MATERIAL[estado] || '#6c757d';
}

/**
 * Obtiene la clase CSS de Bootstrap para un estado específico
 * @param {string} estado - El estado del albarán o material
 * @returns {string} - La clase CSS de Bootstrap correspondiente
 */
export function getEstadoBadgeClass(estado) {
  const classes = {
    // Estados de Albarán
    'pendiente_asignar': 'badge-warning',
    'asignado': 'badge-secondary',
    'entregado': 'badge-info',
    'devuelto': 'badge-success',
    'pendiente_aceptar_devolucion': 'badge-secondary',
    'cerrado': 'badge-secondary',
    'cerrado_sin_tarea': 'badge-secondary',
    'incidencia': 'badge-danger',
    'material_abandonado': 'badge-secondary',
    
    // Estados de Material
    'pendiente_aceptar': 'badge-warning',
    'entregado': 'badge-info',              // Azul (igual que albarán)
    'devuelto': 'badge-success',            // Verde (igual que albarán)
    'rechazado': 'badge-danger',
    'pendiente_aceptar_devolucion': 'badge-secondary', // Morado (igual que albarán)
    'no_devuelto': 'badge-danger',
    'consumido': 'badge-info',
    'incidencia': 'badge-danger'            // Rojo (igual que albarán)
  };
  
  return classes[estado] || 'badge-secondary';
}

/**
 * Obtiene el texto legible para un estado específico
 * @param {string} estado - El estado del albarán o material
 * @returns {string} - El texto legible correspondiente
 */
export function getEstadoTexto(estado) {
  const textos = {
    // Estados de Albarán
    'pendiente_asignar': 'Sin Asignar',
    'asignado': 'Asignado',
    'entregado': 'Entregado',
    'devuelto': 'Devuelto',
    'cerrado': 'Cerrado',
    'cerrado_sin_tarea': 'Cerrado sin Tarea',
    'pendiente_aceptar_devolucion': 'Revisar Devolución',
    'incidencia': 'Incidencia',
    'material_abandonado': 'Material Abandonado',
    
    // Estados de Material
    'pendiente_aceptar': 'Pendiente Aceptar',
    'rechazado': 'Rechazado',
    'no_devuelto': 'No Devuelto',
    'consumido': 'Consumido',
    'incidencia': 'Incidencia'
  };
  
  return textos[estado] || estado;
}
